import { forwardRef, useCallback, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import MyAssignmentTracksWidget, {
  type TMyAssignmentTracksItem,
} from '@crehana/compass.dashlets/my-assignment-tracks-widget';
import { createUrlWithSourceQueryParams } from '@crehana/utils';

import { useMyLearningPathsQuery } from '../hooks/use-my-learning-paths-query/use-my-learning-paths-query';

type TMyLearningPathsWidgetProps = {
  dataTestId?: string;
  amplitudeSource: string;
  organizationSlug: string;
};

const MyLearningPathsWidget = forwardRef<
  HTMLDivElement,
  TMyLearningPathsWidgetProps
>(
  (
    { dataTestId, amplitudeSource, organizationSlug },
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { logEvent } = useAmplitude();
    const [search, setSearch] = useState('');
    const {
      learningPaths,
      tracksStarted,
      error,
      loading: loadingLearningPaths,
      loadingMore: loadingMoreLearningPaths,
      refetch: refetchLearningPaths,
      fetchMoreLearningPaths,
    } = useMyLearningPathsQuery({
      search,
    });

    const handleViewAllTracks = () => {
      window.location.href = createUrlWithSourceQueryParams({
        url: `/org/${organizationSlug}/learn/my-courses/tracks/`,
        source: amplitudeSource,
      });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
    const handleSelectTrack = useCallback(
      (track: TMyAssignmentTracksItem, order: number) => {
        logEvent({
          event: 'HomeUser Action Track Continued',
          data: {
            ...getSourceEventParams(),
            'Track Id': track.id,
            'Track Progress': track.progress,
            'Track Order': order,
          },
        });

        window.location.href = createUrlWithSourceQueryParams({
          url: `/org/${organizationSlug}/learn/my-courses/tracks/${track.id}/`,
          source: amplitudeSource,
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [amplitudeSource, organizationSlug],
    );

    return (
      <div ref={ref} data-testid={dataTestId} className="tw-w-full tw-h-full">
        <MyAssignmentTracksWidget
          tracks={learningPaths}
          isLoading={loadingLearningPaths}
          isError={!!error}
          isLoadingMore={loadingMoreLearningPaths}
          onSearch={setSearch}
          onRetry={() => refetchLearningPaths()}
          onLoadMore={fetchMoreLearningPaths}
          tracksStarted={tracksStarted}
          onSelectTrack={handleSelectTrack}
          onViewAllTracks={handleViewAllTracks}
        />
      </div>
    );
  },
);

MyLearningPathsWidget.displayName = 'MyLearningPathsWidget';

export { MyLearningPathsWidget };
